import { useEffect } from "react";
import { AlertCircle } from "lucide-react";

import { i18n } from "@lib/i18n";
import { clearLogin } from "@lib/utils/clearLogin";
import { type ErrorComponentProps, useRouter } from "@tanstack/react-router";

const USER_INFO_ERROR_MESSAGE = "GetUserInfo";
const SIGN_IN_REDIRECT_DELAY_MS = 10_000;

function isUserInfoError(error: unknown) {
    return error instanceof Error && error.message === USER_INFO_ERROR_MESSAGE;
}

function formatError(error: unknown) {
    let name: string;
    let message: string;
    let stack: string | undefined;

    if (error instanceof Error) {
        name = error.name || "Error";
        message = error.message || "(empty)";
        stack = error.stack;
    } else if (typeof error === "string") {
        name = "Error";
        message = error || "(empty)";
    } else if (error && typeof error === "object") {
        const details = error as { name?: unknown; message?: unknown; stack?: unknown };
        name = typeof details.name === "string" && details.name ? details.name : "Unknown error";
        message =
            typeof details.message === "string" ? details.message || "(empty)" : "(not provided)";
        stack = typeof details.stack === "string" ? details.stack : undefined;
    } else {
        name = "Unknown error";
        message = String(error);
    }

    const sections = [`Name: ${name}`, `Message: ${message}`];

    if (stack) {
        const stackLines = stack.split("\n");
        const firstStackLine = stackLines[0]?.trim();
        const stackHeader = `${name}: ${message}`;

        if (firstStackLine === stackHeader || (message === "(empty)" && firstStackLine === name)) {
            stackLines.shift();
        }

        const stackDetails = stackLines.join("\n").trim();
        if (stackDetails) sections.push(`Stack:\n${stackDetails}`);
    }

    return sections.join("\n\n");
}

function redirectToSignIn() {
    clearLogin();
    window.location.replace("/signin");
}

export default function ErrorPage({ error, reset }: ErrorComponentProps) {
    const router = useRouter();
    const shouldRedirectToSignIn = isUserInfoError(error);

    useEffect(() => {
        if (!shouldRedirectToSignIn) return;

        const timeout = window.setTimeout(redirectToSignIn, SIGN_IN_REDIRECT_DELAY_MS);
        return () => window.clearTimeout(timeout);
    }, [shouldRedirectToSignIn]);

    function retry() {
        const invalidation = router.invalidate({ forcePending: true });
        reset();
        void invalidation.catch(() => undefined);
    }

    return (
        <main className='flex min-h-screen items-center justify-center bg-white px-6 py-12'>
            <section
                aria-labelledby='error-page-title'
                className='flex w-full max-w-lg flex-col items-center text-center'
            >
                <AlertCircle aria-hidden='true' className='mb-6 size-10 text-red-600' />
                <h1
                    className='typography-heading-l-semibold mb-3 text-gray-900'
                    id='error-page-title'
                >
                    {i18n.error_page_title()}
                </h1>
                <p className='typography-body-m-regular mb-8 text-gray-700'>
                    {i18n.error_page_description()}
                </p>
                {shouldRedirectToSignIn ? (
                    <p className='typography-body-s-regular mb-4 text-gray-600'>
                        {i18n.error_page_redirecting()}
                    </p>
                ) : null}
                <div className='mb-8 w-full text-left'>
                    <h2 className='typography-body-s-semibold mb-2 text-gray-700'>
                        {i18n.error_page_details()}
                    </h2>
                    <pre className='max-h-64 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-4 font-mono text-xs leading-5 wrap-break-word whitespace-pre-wrap text-gray-800'>
                        <code>{formatError(error)}</code>
                    </pre>
                </div>
                <div className='flex flex-wrap justify-center gap-3'>
                    <button
                        className='typography-body-m-semibold rounded-md bg-blue-600 px-5 py-3 text-white hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:outline-none'
                        onClick={retry}
                        type='button'
                    >
                        {i18n.error_page_retry()}
                    </button>
                    <button
                        className='typography-body-m-semibold rounded-md border border-gray-300 px-5 py-3 text-gray-900 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:outline-none'
                        onClick={redirectToSignIn}
                        type='button'
                    >
                        {i18n.error_page_signin()}
                    </button>
                </div>
            </section>
        </main>
    );
}
